// DOM event listener that produces a pollable InputFrame.
// Yaw is owned here (mouse-look + right-stick); sim consumes the resulting aimYaw.

import { CONFIG } from '../config';
import { Gamepad } from './Gamepad';
import { InputBtn, type InputFrame, emptyInput, setBtn } from './InputFrame';
import { TouchControls, isTouchPrimaryDevice } from './TouchControls';

// Hysteresis: touch flips on instantly; flips off only after MODALITY_RELEASE_MS
// of no touch events AND a non-trivial keyboard/mouse/gamepad event. This
// keeps the overlay from flickering off when a hand brushes a trackpad on a
// hybrid device, while staying responsive when the user genuinely switches.
const MODALITY_RELEASE_MS = 3000;
const MOUSE_MOVE_NONTRIVIAL_PX = 4;

export type InputModality = 'touch' | 'kbm' | 'gamepad';

export class Input {
  private keys = new Set<string>();
  private mouseDownL = false;
  private mouseDownR = false;
  private yaw = 0;
  private pitch = 0;
  private pointerLocked = false;
  private resetRequested = false;
  private gamepad = new Gamepad();
  /** Public so PauseController can rewire the mobile ⋯ menu button to
   *  open the in-match settings modal. The default handler (toggle the
   *  released state) stays in place for any future callsite without a
   *  PauseController; PauseController.start() swaps it for the duration
   *  of the match and restores on stop(). */
  readonly touch = new TouchControls();
  // Buttons set in this mask are FORCED OFF in every poll() — the tutorial
  // uses it to lock controls until the player has been introduced to them.
  // Bit positions match InputBtn values; default 0 (no buttons blocked).
  private blockedButtons = 0;
  // When released, input is suppressed so the user can use the debug panel
  // without clicks / keystrokes reaching the character. Click the canvas (or
  // press Escape again) to re-capture control.
  private released = false;
  private releaseListeners: Array<(released: boolean) => void> = [];
  // One-shot edge flags for spectate cycling. Arrow keys + gamepad left-stick
  // horizontal clicks both bump these; the HUD layer drains them each frame.
  private spectateLeftRequested = 0;
  private spectateRightRequested = 0;
  private prevGamepadLX = 0;

  // Modality tracking — drives the touch overlay's visibility and a few
  // platform-specific branch points (skip pointer-lock on touch).
  private modality: InputModality;
  private lastTouchEventTs = 0;

  // Handlers stored as fields so dispose() can pass the exact function
  // references back to removeEventListener — anonymous listeners would leak
  // across rapid restart cycles (editor playtest, lobby → match → lobby).
  private onContextMenu = (e: MouseEvent): void => e.preventDefault();
  private onCanvasPointerUp = (e: PointerEvent): void => {
    // Skip pointer-lock for touch — Android Chrome rejects requestPointerLock
    // from a synthetic-click after touchend, and iOS Safari has no concept
    // of pointer lock at all.
    if (e.pointerType === 'touch') return;
    if (this.released) this.setReleased(false);
    this.requestPointerLock();
  };
  private onPointerLockChange = (): void => {
    this.pointerLocked = document.pointerLockElement === this.canvas;
    // If the browser dropped pointer lock (user hit ESC natively), treat
    // that as a "release control" request too — matches user expectation.
    // Skip on touch devices where pointer lock isn't a concept.
    if (!this.pointerLocked && !this.released && this.modality !== 'touch') {
      this.setReleased(true);
    }
  };
  private onTouchStart = (): void => {
    this.lastTouchEventTs = performance.now();
    if (this.modality !== 'touch') this.setModality('touch');
  };
  private onTouchMoveOrEnd = (): void => {
    this.lastTouchEventTs = performance.now();
  };

  constructor(private canvas: HTMLCanvasElement) {
    addEventListener('keydown', this.onKeyDown);
    addEventListener('keyup', this.onKeyUp);
    addEventListener('mousedown', this.onMouseDown);
    addEventListener('mouseup', this.onMouseUp);
    addEventListener('mousemove', this.onMouseMove);
    addEventListener('contextmenu', this.onContextMenu);
    addEventListener('touchstart', this.onTouchStart, { passive: true });
    addEventListener('touchmove', this.onTouchMoveOrEnd, { passive: true });
    addEventListener('touchend', this.onTouchMoveOrEnd, { passive: true });
    canvas.addEventListener('pointerup', this.onCanvasPointerUp);
    document.addEventListener('pointerlockchange', this.onPointerLockChange);

    // Wire touch overlay edge events back through the same buffers the
    // keyboard/gamepad spectate paths feed.
    this.touch.onSpectateLeft = () => { this.spectateLeftRequested += 1; };
    this.touch.onSpectateRight = () => { this.spectateRightRequested += 1; };
    this.touch.onMenuTap = () => { this.setReleased(!this.released); };

    this.modality = isTouchPrimaryDevice() ? 'touch' : 'kbm';
    this.touch.setVisible(this.modality === 'touch');
  }

  /** Detach every listener this Input attached. Required before discarding
   *  an Input instance — the editor's playtest flow constructs a fresh
   *  Input each time, and leaked listeners accumulate into doubled input
   *  frames / phantom yaw drift across ~5 restart cycles. */
  dispose(): void {
    removeEventListener('keydown', this.onKeyDown);
    removeEventListener('keyup', this.onKeyUp);
    removeEventListener('mousedown', this.onMouseDown);
    removeEventListener('mouseup', this.onMouseUp);
    removeEventListener('mousemove', this.onMouseMove);
    removeEventListener('contextmenu', this.onContextMenu);
    removeEventListener('touchstart', this.onTouchStart);
    removeEventListener('touchmove', this.onTouchMoveOrEnd);
    removeEventListener('touchend', this.onTouchMoveOrEnd);
    this.canvas.removeEventListener('pointerup', this.onCanvasPointerUp);
    document.removeEventListener('pointerlockchange', this.onPointerLockChange);
    this.touch.dispose();
    // Exit pointer lock if we currently hold it so the next Input instance
    // starts in a clean state.
    if (document.pointerLockElement === this.canvas) document.exitPointerLock?.();
    this.releaseListeners.length = 0;
  }

  /** Show or hide the touch overlay's spectate L/R buttons. Driven from
   *  main.ts using the same localDead && phase==='playing' check that gates
   *  the spectate-edge consumption — same UX semantics across all input
   *  modalities. */
  setSpectateVisible(v: boolean): void {
    this.touch.setSpectateVisible(v);
  }

  /** Update the JumpKick button's label between "KICK" (mid-air) and
   *  "HEADBUTT" (planted) — purely cosmetic, the binding doesn't change. */
  setLocalMidAir(midAir: boolean): void {
    this.touch.setMidAir(midAir);
  }

  /** Flip the mobile Punch button's label to "THROW" while the local
   *  player is double-handing a victim. Same affordance reasoning as
   *  setLocalMidAir — the binding's behavior changes, so the label should. */
  setLocalDoubleHolding(holding: boolean): void {
    this.touch.setDoubleHolding(holding);
  }

  getModality(): InputModality { return this.modality; }

  private setModality(m: InputModality): void {
    if (this.modality === m) return;
    this.modality = m;
    this.touch.setVisible(m === 'touch');
  }

  /** Check whether enough time has passed since the last touch to flip away
   *  from touch modality. Called from the keyboard/mouse/gamepad paths. */
  private maybeFlipAwayFromTouch(targetModality: 'kbm' | 'gamepad'): void {
    if (this.modality !== 'touch') {
      // Already non-touch; just refine which.
      if (this.modality !== targetModality) this.modality = targetModality;
      return;
    }
    if (performance.now() - this.lastTouchEventTs > MODALITY_RELEASE_MS) {
      this.setModality(targetModality);
    }
  }

  isReleased(): boolean { return this.released; }

  onReleaseChange(cb: (released: boolean) => void): void {
    this.releaseListeners.push(cb);
    cb(this.released);
  }

  setReleased(v: boolean): void {
    if (this.released === v) return;
    this.released = v;
    if (v) {
      // Drop stale keys/buttons so a held key doesn't keep firing when we
      // re-acquire control.
      this.keys.clear();
      this.mouseDownL = false;
      this.mouseDownR = false;
      if (this.pointerLocked) document.exitPointerLock?.();
    }
    for (const cb of this.releaseListeners) cb(v);
  }

  consumeReset(): boolean {
    const r = this.resetRequested;
    this.resetRequested = false;
    return r;
  }

  /** One-shot edge flags for spectate-mode focus cycling. The HUD layer
   *  drains these each frame while the local player is dead. */
  consumeSpectateLeft(): number {
    const n = this.spectateLeftRequested;
    this.spectateLeftRequested = 0;
    return n;
  }
  consumeSpectateRight(): number {
    const n = this.spectateRightRequested;
    this.spectateRightRequested = 0;
    return n;
  }

  /** Camera-relative yaw — accumulated from mouse movement. */
  cameraYaw(): number { return this.yaw; }
  /** Camera pitch in radians. Positive = camera tilted up (looking down at character). */
  cameraPitch(): number { return this.pitch; }

  /** Seed the camera yaw — useful when a non-arena spawn (e.g. tutorial)
   *  places the player facing a non-default world direction and the default
   *  yaw=0 would put the camera in a misaligned orientation. */
  setCameraYaw(yaw: number): void { this.yaw = yaw; }

  poll(tick: number, fixedDt: number): InputFrame {
    const gp = this.gamepad.poll(fixedDt);
    // Gamepad Start is now the Pause / Settings menu binding (handled by
    // PauseController). The Gamepad still exposes resetEdge so a future
    // debug rebind can read it; we just no longer fire forceReset() on it.
    if (gp.active) this.maybeFlipAwayFromTouch('gamepad');

    const tc = this.touch.poll(fixedDt);

    // Gamepad LS X → spectate cycle edge. Threshold at ±0.55 with a hysteresis
    // reset band at ±0.25 so a diagonal hold doesn't spam cycles every frame.
    const lx = gp.moveX;
    const PUSH = 0.55, RELEASE = 0.25;
    if (lx > PUSH && this.prevGamepadLX <= PUSH) this.spectateRightRequested += 1;
    if (lx < -PUSH && this.prevGamepadLX >= -PUSH) this.spectateLeftRequested += 1;
    // Only "latch" as pushed while past PUSH; drop back below RELEASE to arm again.
    if (Math.abs(lx) < RELEASE) this.prevGamepadLX = 0;
    else this.prevGamepadLX = lx;

    // While released, return neutral input so the character does nothing
    // while the user is interacting with the debug panel. Still poll the
    // gamepad so the reset-edge is honoured.
    if (this.released) {
      return emptyInput(tick);
    }

    this.yaw += gp.yawDelta + tc.yawDelta;
    this.pitch = clampPitch(this.pitch + gp.pitchDelta + tc.pitchDelta);

    const frame: InputFrame = emptyInput(tick);

    // Movement: prefer touch joystick or gamepad stick when active, else WASD.
    let mx = 0, mz = 0;
    if (tc.moveX !== 0 || tc.moveZ !== 0) {
      mx = tc.moveX;
      mz = tc.moveZ;
    } else if (gp.moveX !== 0 || gp.moveZ !== 0) {
      mx = gp.moveX;
      mz = gp.moveZ;
    } else {
      if (this.keys.has('KeyW')) mz -= 1;
      if (this.keys.has('KeyS')) mz += 1;
      if (this.keys.has('KeyA')) mx -= 1;
      if (this.keys.has('KeyD')) mx += 1;
    }
    const len = Math.hypot(mx, mz);
    if (len > 1) { mx /= len; mz /= len; }
    frame.moveX = mx;
    frame.moveZ = mz;
    frame.aimYaw = this.yaw;

    // LMB = Punch (Character.ts alternates arms while held).
    // RMB = Headbutt / jump-kick.
    // E   = Grab (engages both arms while held).
    setBtn(frame, InputBtn.Punch, this.mouseDownL);
    setBtn(frame, InputBtn.Grab, this.keys.has('KeyE'));
    setBtn(frame, InputBtn.Jump, this.keys.has('Space'));
    setBtn(frame, InputBtn.Sprint, this.keys.has('ShiftLeft') || this.keys.has('ShiftRight'));
    setBtn(frame, InputBtn.JumpKick, this.mouseDownR);
    // Q = optional throw backup, only acts when sim sees us double-handing
    // a victim (Character.ts checks grab.isDoubleHolding() at the trigger).
    // Useful for trackpad users who can't chord LMB + RMB.
    setBtn(frame, InputBtn.Throw, this.keys.has('KeyQ'));
    frame.buttons |= gp.buttons;
    frame.buttons |= tc.buttons;
    if (this.blockedButtons) frame.buttons &= ~this.blockedButtons;
    return frame;
  }

  /** Mask of buttons (bit-or'd InputBtn values) that should be forced OFF
   *  in every subsequent poll(). Used by the tutorial to lock specific
   *  controls (e.g. Grab during the combat phase) until the right moment. */
  setBlockedButtons(mask: number): void {
    this.blockedButtons = mask;
  }

  private requestPointerLock(): void {
    if (!this.pointerLocked) this.canvas.requestPointerLock?.();
  }

  private onKeyDown = (e: KeyboardEvent) => {
    // Modifier-only keypresses are ambient (a hand resting on Shift) and
    // don't count as a modality-flip signal.
    const isModifier = e.code === 'ShiftLeft' || e.code === 'ShiftRight'
      || e.code === 'ControlLeft' || e.code === 'ControlRight'
      || e.code === 'AltLeft' || e.code === 'AltRight'
      || e.code === 'MetaLeft' || e.code === 'MetaRight'
      || e.code === 'CapsLock';
    if (!e.repeat && !isModifier) this.maybeFlipAwayFromTouch('kbm');

    // Escape toggles "released" — frees the cursor so the debug panel (or any
    // overlay) is clickable. Click the canvas to reacquire control.
    if (e.code === 'Escape') {
      this.setReleased(!this.released);
      return;
    }
    // Arrow-left / arrow-right → spectate cycle edges (only trigger on the
    // initial press, KeyboardEvent.repeat filters out auto-repeat). The HUD
    // reads these when the local player is dead.
    if (!e.repeat) {
      if (e.code === 'ArrowLeft') this.spectateLeftRequested += 1;
      if (e.code === 'ArrowRight') this.spectateRightRequested += 1;
    }
    this.keys.add(e.code);
    // F2 → debug round reset, dev-only. Replaces the previous gamepad
    // Start binding (Start is now the pause-menu trigger). The matching
    // consumer is LocalTransport.tick — ColyseusTransport ignores it
    // server-side, so the binding is effectively local-match only.
    if (e.code === 'F2' && import.meta.env.DEV && !e.repeat) {
      this.resetRequested = true;
      e.preventDefault();
    }
    if (e.code === 'Space') e.preventDefault();
  };
  private onKeyUp = (e: KeyboardEvent) => { this.keys.delete(e.code); };
  private onMouseDown = (e: MouseEvent) => {
    this.maybeFlipAwayFromTouch('kbm');
    if (this.released) return;
    if (e.button === 0) this.mouseDownL = true;
    if (e.button === 2) this.mouseDownR = true;
  };
  private onMouseUp = (e: MouseEvent) => {
    if (this.released) return;
    if (e.button === 0) this.mouseDownL = false;
    if (e.button === 2) this.mouseDownR = false;
  };
  private onMouseMove = (e: MouseEvent) => {
    // Trackpads jitter sub-pixel even at rest. Only count as a modality
    // signal when movement is genuinely human-scale.
    if (Math.hypot(e.movementX || 0, e.movementY || 0) > MOUSE_MOVE_NONTRIVIAL_PX) {
      this.maybeFlipAwayFromTouch('kbm');
    }
    if (this.released) return;
    const dx = this.pointerLocked ? e.movementX : 0;
    const dy = this.pointerLocked ? e.movementY : 0;
    // Read CONFIG.controls each event so a settings slider drag while the
    // user is mid-look takes effect on the very next frame, no rewire.
    const sens = CONFIG.controls.mouseSensitivity * 0.0025;
    const yInv = CONFIG.controls.invertY ? -1 : 1;
    this.yaw -= dx * sens;
    this.pitch = clampPitch(this.pitch + dy * sens * yInv);
  };
}

// Clamp prevents over-the-top flips and — together with the floor in
// CameraRig — keeps the camera from diving below the arena.
const PITCH_MIN = -0.45; // ~-26°  (camera dips a bit below focus)
const PITCH_MAX = 1.15;  // ~+66°  (near top-down but not inverted)
function clampPitch(p: number): number {
  return Math.max(PITCH_MIN, Math.min(PITCH_MAX, p));
}
